import { ArrowUpRight, Github, Lock } from 'lucide-react';
import { PROJECTS } from '../data/projects';
import { trackEvent } from '../utils/analytics';
import { PageSection, SectionHeader } from './SectionHeader';
import CaseStudies from './CaseStudy';

function ProjectLinks({ project, compact = false }) {
  const size = compact ? 14 : 16;
  return (
    <div className="flex items-center gap-1.5">
      {project.link && (
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${project.name} live site`}
          className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-muted hover:text-void hover:bg-accent hover:border-accent transition-colors"
          onClick={() => trackEvent('project_link_click', { project: project.name, link: 'live' })}
        >
          <ArrowUpRight size={size} />
        </a>
      )}
      {project.github && (
        <a
          href={project.github}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${project.name} on GitHub`}
          className="h-9 w-9 rounded-full border border-line flex items-center justify-center text-muted hover:text-ink hover:border-ink transition-colors"
          onClick={() => trackEvent('project_link_click', { project: project.name, link: 'github' })}
        >
          <Github size={size} />
        </a>
      )}
      {!project.link && !project.github && project.privateNote && (
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-faint">
          <Lock size={12} aria-hidden="true" />
          {project.privateNote}
        </span>
      )}
    </div>
  );
}

function FeaturedCard({ project, index }) {
  return (
    <article
      className={`card card-hover reveal group overflow-hidden flex flex-col ${project.span}`}
      style={{ '--d': `${index * 70}ms` }}
    >
      <div className="grid sm:grid-cols-2 flex-1">
        <div className="p-7 lg:p-9 flex flex-col">
          <div className="flex items-center justify-between gap-4">
            <p className="label">
              <span className="text-accent">{project.num}</span>
              <span className="mx-2 text-line-2">/</span>
              {project.type}
            </p>
            <span className="font-mono text-[11px] text-faint">{project.year}</span>
          </div>
          <h3 className="display mt-6 text-3xl lg:text-4xl">{project.name}</h3>
          <p className="mt-1.5 text-sm text-faint">{project.subtitle}</p>
          <p className="mt-5 text-sm leading-relaxed text-muted">{project.description}</p>

          <ul className="mt-6 grid grid-cols-2 gap-2" aria-label="Key metrics">
            {project.metrics?.slice(0, 4).map((m) => (
              <li key={m} className="flex items-center gap-2 text-xs text-ink">
                <span className="h-1 w-1 rounded-full bg-accent shrink-0" aria-hidden="true" />
                {m}
              </li>
            ))}
          </ul>

          {project.architecture && (
            <p className="mt-6 font-mono text-[11px] leading-relaxed text-faint">
              <span className="text-line-2">→ </span>
              {project.architecture}
            </p>
          )}

          {project.caseStudy?.learned && (
            <blockquote className="mt-8 border-l border-accent/40 pl-4">
              <p className="font-serif italic text-lg leading-snug text-ink/90">
                &ldquo;{project.caseStudy.learned}&rdquo;
              </p>
            </blockquote>
          )}

          <div className="mt-auto pt-8 flex items-end justify-between gap-4">
            <ul className="flex flex-wrap gap-1.5">
              {project.tech.slice(0, 5).map((t) => (
                <li key={t} className="chip">{t}</li>
              ))}
            </ul>
            <ProjectLinks project={project} />
          </div>
        </div>

        <div className="relative min-h-[380px] sm:min-h-0 overflow-hidden bg-[#efe8dc]">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/50 via-transparent to-transparent z-10 pointer-events-none" />
          {/* Screenshot is a marketing frame: shift it up so only the phone is in view. */}
          <div className="absolute left-1/2 -translate-x-1/2 top-6 w-[86%] max-w-[340px] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-3">
            <img
              src={project.img}
              alt={`${project.name} app screenshot`}
              loading="lazy"
              className="w-full drop-shadow-[0_30px_60px_rgba(0,0,0,0.45)]"
              style={{ transform: 'translateY(-22.5%)' }}
            />
          </div>
        </div>
      </div>
    </article>
  );
}

function Card({ project, index }) {
  const wide = project.span?.includes('col-span-12');
  return (
    <article
      className={`card card-hover reveal group flex flex-col p-7 ${project.span}`}
      style={{ '--d': `${index * 70}ms` }}
    >
      <div className="flex items-center justify-between gap-4">
        <p className="label">
          <span className="text-accent">{project.num}</span>
          <span className="mx-2 text-line-2">/</span>
          {project.type}
        </p>
        <span className="font-mono text-[11px] text-faint">{project.year}</span>
      </div>

      <div className={wide ? 'mt-5 grid md:grid-cols-[1fr_1.6fr] gap-6 items-start' : ''}>
        <div>
          <h3 className="display mt-5 text-2xl">{project.name}</h3>
          <p className="mt-1 text-sm text-faint">{project.subtitle}</p>
        </div>
        <p className={`text-sm leading-relaxed text-muted ${wide ? 'md:mt-5' : 'mt-4'}`}>
          {project.description}
        </p>
      </div>

      {project.img && !wide && (
        <div className="mt-6 -mx-2 rounded-xl overflow-hidden border border-line bg-surface-2">
          <img
            src={project.img}
            alt={`${project.name} screenshot`}
            loading="lazy"
            className="w-full aspect-[2/1] object-cover object-top opacity-90 transition-[opacity,transform] duration-700 group-hover:opacity-100 group-hover:scale-[1.02]"
          />
        </div>
      )}

      {project.metrics && (
        <ul className="mt-5 flex flex-wrap gap-1.5" aria-label="Key metrics">
          {project.metrics.slice(0, 4).map((m) => (
            <li key={m} className="chip-accent">{m}</li>
          ))}
        </ul>
      )}

      {project.architecture && !wide && (
        <p className="mt-5 font-mono text-[11px] leading-relaxed text-faint">
          <span className="text-line-2">→ </span>
          {project.architecture}
        </p>
      )}

      <div className="mt-auto pt-6 flex items-end justify-between gap-4">
        <ul className="flex flex-wrap gap-1.5">
          {project.tech.slice(0, 4).map((t) => (
            <li key={t} className="chip">{t}</li>
          ))}
        </ul>
        <ProjectLinks project={project} compact />
      </div>
    </article>
  );
}

export default function Projects() {
  return (
    <PageSection id="projects">
      <SectionHeader
        index="02"
        label="Selected work"
        title="Things I have <em>built.</em>"
        description="Side projects, research, and a couple of things people actually use. Numbers included where I have them."
      />

      <div className="grid gap-4 lg:grid-cols-12 lg:auto-rows-[minmax(0,auto)]">
        {PROJECTS.map((p, i) =>
          p.featured ? (
            <FeaturedCard key={p.num} project={p} index={i} />
          ) : (
            <Card key={p.num} project={p} index={i} />
          )
        )}
      </div>

      <CaseStudies />
    </PageSection>
  );
}
